import { useState, useEffect, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { addDocumentInRedux, updateDocTypeList } from "utils/redux/actions";
import { useRequestHook } from "utils/hooks/hooks";
import DocumentsModal from "../DocumentsModal/DocumentsModal";
import ConfirmModal from "../ConfirmModal";
import useAxios from "utils/hooks/useAxios";
import UploadToggler from "../DocumentUploader/UploadToggler";
import UploadDocumentModal from "../DocumentUploader/UploadDocumentModal";
import AdminTools from "components/AdminTools/AdminTools";
import RenderList from "./RenderList";
import AutoLoadModal from "./AutoLoadModal";
import DownloadDoctypes from "./DownloadDoctypes";
import CreateEnvelope from "./CreateEnvelope";

/**
 * Export a component that fetch all documents
 * from the database to extract data.
 */

const LoadDocuments = (props) => {
  const {
    dispatch,
    id: serviceID,
    name: serviceName,
    savedDocument,
    serviceType,
    status,
    mainModule,
    displayReference,
    docTypesList,
    isUserAdmin,
    signatureEditor,
    userId,
    serviceModules,
  } = props;
  const [isModalOpen, setIsModalOpen] = useState(() => false);
  const [uploadDocumentOpen, setUploadDocumentOpen] = useState(() => false);
  const [confirmDelete, setConfirmDelete] = useState(() => false);
  const [confirmCancel, setConfirmCancel] = useState(() => false);
  const [confirmRelease, setConfirmRelease] = useState(() => false);
  const [failRelease, setFailRelease] = useState(false);
  const [modalFailMessage, setModalFailMessage] = useState("");
  const [failCancel, setFailCancel] = useState(false);
  const [document, setDocument] = useState();
  const [documentsList, setDocumentsList] = useState(() => []);
  const [listChange, setListChange] = useState(0);
  const [docId] = useState(() => {
    let search = window.location.search;
    let params = new URLSearchParams(search);
    let docId = params.get("docId");
    return docId;
  });
  const { handleRequest } = useRequestHook(); //Define method to fetch data.

  const { modules_on_finish: modulesOnFinish } = serviceModules;

  const toggleModal = useCallback(
    () => setIsModalOpen((isOpen) => !isOpen),
    []
  );

  const triggerListChange = () => {
    setListChange((prev) => prev + 1);
  };

  const handleCloseModal = () => {
    setFailRelease(false);
    setFailCancel(false);
    setModalFailMessage("");
    triggerListChange();
  };

  const handleDeleteDocument = (id) => {
    handleRequest(
      "post",
      "/documents/delete-document",
      { id: id, userId },
      (isDeleted) => {
        setConfirmDelete(false);
        if (isDeleted) {
          setDocumentsList((oldDocList) =>
            oldDocList.filter(({ documentId }) => documentId !== id)
          );
          documentsList.length === 0 && triggerListChange();
        }
      }
    );
  };

  const handleCancelDocument = (id) => {
    handleRequest(
      "post",
      "/documents/cancel-document",
      { docId: id },
      ({ canceled, error }) => {
        if (!canceled) {
          setFailCancel(true);
          setModalFailMessage(error);
        }

        setConfirmCancel(false);

        if (canceled) {
          triggerListChange();
        }
      }
    );
  };

  const handleReleaseDocument = (id) => {
    if (id !== savedDocument.id) {
      handleRequest(
        "post",
        "/documents/load-selected-document",
        {
          documentId: id,
          serviceType: serviceType,
          displayReference: displayReference,
          pagesToRequest: [0],
          docTypesList,
        },
        (response) => {
          if (response) {
            saveDoc(response);
          }
        }
      );
    } else {
      saveDoc(savedDocument);
    }
  };

  const saveDoc = (document) => {
    const { thumbnails, pagesLength, reference, additionalFields, id } =
      document;
    handleRequest(
      "post",
      "/thumbnails/update-thumbnails",
      {
        thumbnails,
        pagesLength,
        documentId: id,
        approved: true,
        reference,
        additionalFields,
      },
      (response, msg) => {
        if (!response) {
          setFailRelease(true);
          setConfirmRelease(false);
          setModalFailMessage(msg);
          return;
        }
        setDocumentsList((oldDocList) =>
          oldDocList.filter(({ documentId }) => documentId !== id)
        );
        setConfirmRelease(false);
        dispatch(addDocumentInRedux()); //empty the docment in redux
      }
    );
  };

  const getTypesData = useMemo(
    () => ({ typesLists: mainModule["-lists"], serviceID }),
    [mainModule, serviceID]
  );
  const { responsedData: responsedTypes } = useAxios({
    method: "post",
    url: "/documents-types/load-allowed-types",
    data: getTypesData,
    immediate: !!mainModule["-lists"]?.length,
  });

  useEffect(() => {
    /**
     * Add document types in redux state.
     */
    if (responsedTypes) dispatch(updateDocTypeList(responsedTypes));
  }, [responsedTypes]); // eslint-disable-line
  return (
    <>
      {["signature_docs", "detect_types"].includes(serviceType) &&
        status === "0" && (
          <UploadToggler setUploadDocumentOpen={setUploadDocumentOpen} />
        )}
      {["detect_types"].includes(serviceType) && isUserAdmin && (
        <DownloadDoctypes
          getTypesData={getTypesData}
          serviceName={serviceName}
        />
      )}
      {["signature_envelopes", "detect_types"].includes(serviceType) && (
        <AdminTools
          toggleModal={toggleModal}
          serviceID={serviceID}
          serviceType={serviceType}
          displayReference={displayReference}
          isUserAdmin={isUserAdmin}
        />
      )}
      {["signature_envelopes"].includes(serviceType) &&
        signatureEditor &&
        status === "0" && (
          <CreateEnvelope />
        )}
      <RenderList
        serviceID={serviceID}
        serviceType={serviceType}
        status={status}
        documentsList={documentsList}
        setDocumentsList={setDocumentsList}
        displayReference={displayReference}
        toggleModal={toggleModal}
        setConfirmDelete={setConfirmDelete}
        setConfirmCancel={setConfirmCancel}
        setDocument={setDocument}
        setConfirmRelease={setConfirmRelease}
        mainModule={mainModule}
        signatureEditor={signatureEditor}
        listChange={listChange}
      />
      {isModalOpen && (
        <DocumentsModal
          isModalOpen={isModalOpen}
          toggleModal={toggleModal}
          mainModule={mainModule}
          displayReference={displayReference}
          serviceType={serviceType}
          updateDocumentsList={setDocumentsList}
          modulesOnFinish={modulesOnFinish}
        />
      )}
      {uploadDocumentOpen && (
        <UploadDocumentModal
          serviceID={serviceID}
          setUploadDocumentOpen={setUploadDocumentOpen}
          serviceType={serviceType}
          triggerListChange={triggerListChange}
        />
      )}
      {confirmDelete && (
        <ConfirmModal
          message={"Möchten Sie das Dokument wirklich löschen?"}
          confirmFunction={() => handleDeleteDocument(document)}
          closeModal={() => setConfirmDelete(false)}
        />
      )}
      {confirmCancel && (
        <ConfirmModal
          message={"Möchten Sie den Vorgang wirklich abrechen?"}
          confirmFunction={() => handleCancelDocument(document)}
          closeModal={() => setConfirmCancel(false)}
        />
      )}
      {confirmRelease && (
        <ConfirmModal
          message={"Möchten Sie das Dokument wirklich freigeben?"}
          confirmFunction={() => handleReleaseDocument(document)}
          closeModal={() => setConfirmRelease(false)}
        />
      )}
      {failRelease ||
        (failCancel && (
          <ConfirmModal
            message={modalFailMessage}
            closeModal={() => handleCloseModal()}
          />
        ))}
      <AutoLoadModal
        toggleModal={toggleModal}
        serviceID={serviceID}
        serviceType={serviceType}
        displayReference={displayReference}
        docId={docId}
        status={status}
        signatureEditor={signatureEditor}
      />
    </>
  );
};

const mapStateToProps = ({
  docTypesList,
  savedDocument,
  userData: { isUserAdmin, id: userId },
}) => ({ docTypesList, savedDocument, isUserAdmin, userId });

export default connect(mapStateToProps)(LoadDocuments); //Export component and connect it with redux.
